import { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation } from "@apollo/react-hooks";
import { useHistory } from "react-router-dom";

import {
  GetShippingProfiles,
  GetOwnedProducts,
  GetShippingDetail,
  GetStatesList
} from "../queries";
import useInputState from "../../../../hooks/useInputState";
import {
  EditShippingProfile,
  DeleteShippingProfile,
  createShippingZone
} from "../mutation";
import {
  clubProductVariants,
  removeEmptyChildren,
  parseNetworkError
} from "./helpers";
import usePreviousValue from "../../../../hooks/usePreviousValue";

export const useShippingDetail = (id) => {
  const { data, loading } = useQuery(GetShippingDetail, {
    variables: { id },
    skip: !id
  });

  const shippingProfile = (data && data.shippingProfile) || {};

  const selectedProductIds =
    (shippingProfile.products &&
      shippingProfile.products.map(({ id }) => id)) ||
    [];

  const productsList = clubProductVariants(shippingProfile.products);
  const selectedStates =
    shippingProfile.shippingZones &&
    shippingProfile.shippingZones.reduce(
      (acc, zone) => acc.concat(zone.states.map((state) => state.id)),
      []
    );

  return {
    loading,
    selectedProductIds,
    shippingProfile: {
      ...shippingProfile,
      products: productsList
    },
    selectedStates
  };
};

export const useChangeShippingProfile = ({
  selectedProductIds,
  shippingProfileId,
  name
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchText, setSearchText] = useInputState("");
  const [productIds, setProductIds] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [productName, setProductName] = useInputState(name);
  const previousProductName = usePreviousValue(name);

  useEffect(() => {
    selectedProductIds && setProductIds(selectedProductIds);
  }, [selectedProductIds]);

  useEffect(() => {
    name && setProductName(name);
  }, [name]);

  const [updateShippingProfile] = useMutation(EditShippingProfile, {
    refetchQueries: [
      {
        query: GetShippingDetail,
        variables: {
          id: shippingProfileId
        }
      },
      { query: GetOwnedProducts }
    ]
  });

  const onCloseDialog = () => {
    setSearchText("");
    setIsModalOpen(false);
    setProductIds(selectedProductIds);
    setIsSaving(false);
  };

  const editShippingProfile = async () => {
    try {
      setIsSaving(true);
      await updateShippingProfile({
        variables: {
          shippingProfile: {
            id: shippingProfileId,
            name: productName,
            productIds
          }
        }
      });
      onCloseDialog();
    } catch (err) {
      setIsSaving(false);
    }
  };

  const removeShippingProfileProduct = async (idsToRemove) => {
    const keep = selectedProductIds.filter((id) => !idsToRemove.includes(id));
    setIsSaving(true);
    try {
      await updateShippingProfile({
        variables: {
          shippingProfile: {
            id: shippingProfileId,
            productIds: keep
          }
        }
      });
      setIsSaving(false);
    } catch (err) {
      setIsSaving(false);
    }
  };

  return {
    isModalOpen,
    setIsModalOpen,
    searchText,
    setSearchText,
    removeShippingProfileProduct,
    onCloseDialog,
    isSaving,
    productIds,
    editShippingProfile,
    setProductIds,
    productName,
    setProductName,
    saveEnabled: previousProductName !== productName
  };
};

export const useRemoveShippingProfile = (profileId) => {
  const [isAlertOpen, setIsAlertOpen] = useState(false);

  const history = useHistory();

  const [removeShippingProfile] = useMutation(DeleteShippingProfile, {
    refetchQueries: [
      { query: GetOwnedProducts },
      { query: GetShippingProfiles }
    ]
  });

  const onRemoveConfirmed = async () => {
    try {
      await removeShippingProfile({
        variables: {
          id: profileId
        }
      });
      reset();
      history.push("/settings/shipping");
    } catch (err) {
      reset();
    }
  };

  const reset = () => {
    setIsAlertOpen(false);
  };

  return {
    isAlertOpen,
    onRemoveConfirmed,
    setIsAlertOpen
  };
};

export const useCreateShippingZone = (shippingProfileId) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [error, setError] = useState();
  const [stateIds, setStateIds] = useState([]);
  const [searchText, setSearchText] = useInputState("");
  const [zoneName, setZoneName] = useInputState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isRestOfWorld, setIsRestOfWorld] = useState(false);
  const onToggleRestOfWorld = () => {
    !isRestOfWorld && setStateIds([]);
    setIsRestOfWorld((state) => !state);
  };
  useEffect(() => {
    stateIds.length && setIsRestOfWorld(false);
  }, [stateIds]);

  const [saveShippingZone] = useMutation(createShippingZone, {
    variables: {
      shippingZone: {
        shippingProfileId,
        name: zoneName,
        stateIds,
        isRestOfWorld
      }
    },
    refetchQueries: [
      {
        query: GetShippingDetail,
        variables: {
          id: shippingProfileId
        }
      }
    ]
  });

  const onCloseDialog = () => {
    setSearchText("");
    setStateIds([]);
    setZoneName("");
    setIsModalOpen(false);
    setIsRestOfWorld(false);
    setIsSaving(false);
    setError();
  };

  const onCreateShippingZone = async () => {
    setIsSaving(true);
    setError();
    try {
      await saveShippingZone();
      onCloseDialog();
    } catch (e) {
      setError(parseNetworkError(e));
      setIsSaving(false);
    }
  };
  return {
    isModalOpen,
    setIsModalOpen,
    onCloseDialog,
    stateIds,
    setStateIds,
    searchText,
    setSearchText,
    zoneName,
    setZoneName,
    onCreateShippingZone,
    isSubmitDisabled: !(zoneName && (stateIds.length || isRestOfWorld)),
    isSaving,
    onToggleRestOfWorld,
    isRestOfWorld,
    error
  };
};

export const useStateList = (
  searchText = "",
  isModalOpen,
  selectedStates,
  currentStates = []
) => {
  const { data, loading } = useQuery(GetStatesList, {
    skip: !isModalOpen
  });
  const statesList = useMemo(
    () =>
      data &&
      data.regions &&
      removeEmptyChildren(
        data.regions.map((region) => ({
          text: region.name,
          value: region.id,
          children: removeEmptyChildren(
            region.countries.map((country) => ({
              text: country.name,
              value: country.id,
              children: country.states
                .filter(
                  (state) =>
                    (country.name
                      .toLowerCase()
                      .includes(searchText.toLowerCase()) ||
                      state.name
                        .toLowerCase()
                        .includes(searchText.toLowerCase())) &&
                    (currentStates.includes(state.id) ||
                      !selectedStates.includes(state.id))
                )
                .map((state) => ({
                  text: state.name,
                  value: state.id
                }))
            }))
          )
        }))
      ),
    [data, searchText, selectedStates, currentStates]
  );

  return { statesList, loading };
};
